import React, { useState, useEffect, useRef } from "react";
import MetaTags from "react-meta-tags";
import {
  Col,
  Row,
  Button,
  Table,
  Space,
  Tag,
  Modal,
  Input,
  Typography,
  Dropdown,
  Menu,
  DatePicker,
} from "antd";
import {
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  ClearOutlined,
  SearchOutlined,
  CalendarOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import BreadcrumbComponent from "../../components/BreadCrumb";
import api from "../../helpers";
import * as masterAction from "../../store/Masters/actions";
import SimpleReactValidator from "simple-react-validator";
import { isEmpty } from "lodash";
import moment from "moment";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";

const BCrumb = [{ to: "/", title: "Home" }, { title: "Holiday" }];

const Holiday = () => {
  const [selectedRow, setSelectedRow] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const simpleValidator = useRef(new SimpleReactValidator());
  const [, forceUpdate] = React.useState();
  const [holidayId, setHolidayId] = useState();
  const [downloadLoader, setDownloadLoader] = useState(false);

  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const [holiday, setHoliday] = useState({
    holidayName: "",
    description: "",
    date: "",
  });

  const specificDispatch = useDispatch();
  const dispatch = useDispatch();

  //dropdown state from reducer
  const updateState = useSelector((state) => state.Masters);
  const { masterHolidayList = [] } = updateState;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    specificDispatch(masterAction.loadMasterHoliday({ status: "BOTH" }));
  }, [specificDispatch]);

  // modal handling
  const showModalOpen = (type) => {
    setModalType(type);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setModalType("");
  };

  const onReset = () => {
    setResetLoading(true);
    const currentYear = new Date().getFullYear();

    setTimeout(() => {
      setHoliday({
        holidayName: "",
        description: "",
        date: "",
      });
      setSelectedYear(currentYear);
      setSearchYear(String(currentYear));
      simpleValidator.current.hideMessages();
      forceUpdate(1);
      setResetLoading(false);
      setIsEdit(false);
      setHolidayId(null);
    }, 1000);

    dispatch(
      masterAction.loadMasterHoliday({ year: currentYear, status: "BOTH" })
    );
    setDisabled(false);
  };

  const handleSubmit = () => {
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
      return;
    }

    setAddLoading(true);
    setDisabled(true);
    setTimeout(() => setDisabled(false), 4000);

    const payload = {
      holidayName: holiday.holidayName,
      year: selectedYear,
      date: holiday.date ? dayjs(holiday.date).format("DD-MM-YYYY") : null,
      ...(holiday.description && {
        description: holiday.description,
      }),
    };

    if (!isEdit) {
      api
        .post("/protected/addholiday", payload)
        .then((res) => {
          if (res.status === 200 || res.status === 201) {
            dispatch(masterAction.loadMasterHoliday({ status: "BOTH" }));
            toast.success("Holiday added successfully", {
              containerId: "toast-1",
            });
            setHoliday({ holidayName: "", description: "", date: null });
            simpleValidator.current.hideMessages();
            setAddLoading(false);
            forceUpdate(1);
          } else {
            toast.warning(res?.data?.msg || "");
          }
        })
        .catch((err) => {
          const errorMsg =
            err?.response?.data?.message ||
            "An error occurred while adding the holiday";
          toast.error(errorMsg, {
            containerId: "toast-1",
          });
          setAddLoading(false);
        });
    } else {
      api
        .post(`/protected/updateholiday/${holidayId}`, payload)
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            dispatch(masterAction.loadMasterHoliday({ status: "BOTH" }));
            toast.success("Holiday updated successfully", {
              containerId: "toast-1",
            });
            setHoliday({ holidayName: "", description: "", date: null });
            setIsEdit(false);
            setHolidayId(null);
            simpleValidator.current.hideMessages();
            setAddLoading(false);
            forceUpdate(1);
          } else {
            toast.warning(response?.data?.msg || "");
          }
        })
        .catch((err) => {
          const errorMsg =
            err?.response?.data?.message ||
            "An error occurred while updating the holiday";
          toast.error(errorMsg, { containerId: "toast-1" });
          setAddLoading(false);
        });
    }
  };

  const handleEdit = (holidayData) => {
    setIsEdit(true);
    setHoliday({
      holidayName: holidayData.holidayName,
      date: holidayData.date ? dayjs(holidayData.date, "DD/MM/YYYY") : null,
      // date: holidayData.date,
      description: holidayData.description,
    });
    setSelectedYear(holidayData.year || currentYear);
  };

  //modal input handler
  const handleInput = (e) => {
    const { name, value } = e.target;

    setHoliday((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    simpleValidator.current.hideMessageFor(name);
    forceUpdate(1);
  };

  const columns = [
    {
      title: (
        <span style={{ whiteSpace: "nowrap" }} className="poppins-txt">
          S.No
        </span>
      ),
      dataIndex: "sno",
      width: 5,
      className: "poppins-txt-lg",
    },
    {
      title: <span className="poppins-txt">Action</span>,
      dataIndex: "action",
      width: 70,
      render: (_, record) => (
        <Space style={{ gap: "8px" }}>
          {/* Edit Button */}
          <Button
            style={{
              background: "#34c38f",
              border: "none",
              minWidth: 30,
              height: 30,
              padding: "2px 4px",
              fontSize: "0.75rem",
            }}
            onClick={() => {
              handleEdit(record);
              setHolidayId(record._id);
            }}
          >
            <EditOutlined style={{ color: "#fff", fontSize: 14 }} />
          </Button>

          {/* Delete Button */}
          <Button
            style={{
              background: "#f46a6a",
              border: "none",
              minWidth: 30,
              height: 30,
              padding: "2px 4px",
              fontSize: "0.75rem",
            }}
            onClick={() => {
              showModalOpen("delete");
              setHolidayId(record._id); // single delete
              setSelectedRow([]); // make sure no bulk delete happens
            }}
          >
            <DeleteOutlined style={{ color: "#fff", fontSize: 14 }} />
          </Button>
        </Space>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      className: "poppins-txt-lg",
    },
    {
      title: <span className="poppins-txt">Day</span>,
      dataIndex: "day",
      className: "poppins-txt-lg",
    },
    {
      title: <span className="poppins-txt">Holiday</span>,
      dataIndex: "holidayName",
      className: "poppins-txt-lg",
    },
    {
      title: <span className="poppins-txt">Description</span>,
      dataIndex: "description",
      className: "poppins-txt-lg",
    },
    {
      title: (
        <span style={{ whiteSpace: "nowrap" }} className="poppins-txt">
          Status
        </span>
      ),
      dataIndex: "status",
      width: 100,
      render: (status) => {
        const isActive = status?.toLowerCase() === "active";
        return (
          <Tag
            style={{
              backgroundColor: isActive ? "#e6f4d7" : "#fae1e1",
              color: isActive ? "#34c38f" : "#f46a6a",
              fontWeight: 500,
              width: 60,
              height: 20,
              paddingTop: "2px",
              fontSize: "11px",
              textAlign: "center",
              lineHeight: "16px",
              borderRadius: 4,
              border: "none",
              fontFamily: "Poppins",
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
  ];

  const currentYear = new Date().getFullYear();
  // const [selectedYear, setSelectedYear] = useState(currentYear);
  const years = Array.from({ length: 2 }, (_, i) => currentYear + i);

  const handleMenuClick = (e) => {
    const year = parseInt(e.key, 10);
    setSelectedYear(year); // update state
    dispatch(masterAction.loadMasterHoliday({ year: year, status: "BOTH" }));
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      {years.map((year) => (
        <Menu.Item key={year}>{year}</Menu.Item>
      ))}
    </Menu>
  );

  //search
  const handleSearch = () => {
    const payload = {
      search: String(searchYear),
      status: "BOTH",
    };

    setSearchLoading(true);

    setTimeout(() => {
      setSearchLoading(false);
    }, 500);

    dispatch(masterAction.loadMasterHoliday(payload));
  };

  //download functionality
  const downloadFile = ({ data, fileName, fileType }) => {
    const blob = new Blob([data], { type: fileType });

    const a = document.createElement("a");
    a.download = fileName;
    a.href = window.URL.createObjectURL(blob);
    const clickEvt = new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    a.dispatchEvent(clickEvt);
    a.remove();
  };

  const exportToPdf = () => {
    setDownloadLoader(true);

    // 1. Build the PDF
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "A4",
    });

    // 2. Table header & rows
    const headers = [["S.No", "Date", "Day", "Holiday Name", "Description"]];

    const rows = tableData.map(
      ({ sno, date, day, holidayName, description }) => [
        sno,
        date,
        day,
        holidayName,
        description,
      ]
    );

    // 3. Title and table
    doc.text("Holiday List", 40, 40);
    doc.autoTable({
      head: headers,
      body: rows,
      startY: 60,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [40, 40, 40] },
      margin: { left: 40, right: 40 },
    });

    // 4. Save
    doc.save(`holiday-list-${dayjs().format("YYYY-MM-DD")}.pdf`);
    setDownloadLoader(false);
  };

  const exportToCsv = async () => {
    setDownloadLoader(true);

    const searchFilter = {};

    try {
      const res = await api.post("/protected/getholiday", searchFilter);
      const holiday = res.data?.data || [];

      const mappedData = holiday.map((data, index) => ({
        _id: data._id,
        sno: index + 1,
        holidayName: data.requestName || "--",
        lopHrs: data.requestValue || "--",
        description: data.description || "--",
        status: data.status === 1 ? "Active" : "Inactive",
      }));

      // Only include fields for CSV that you want:
      const headers = ["S.No", "Day", "Date", "Holiday Name", "Description"];

      const csvRows = mappedData.map((item) => [
        item.sno,
        item.date,
        item.day,
        item.holidayName,
        item.description,
      ]);

      const csvContent = [
        headers.join(","),
        ...csvRows.map((row) => row.join(",")),
      ].join("\n");

      downloadFile({
        data: csvContent,
        fileName: "holiday-details.csv",
        fileType: "text/csv",
      });
    } catch (error) {
      if (error) {
        setDownloadLoader(false);
      }
    } finally {
      setDownloadLoader(false);
    }
  };

  //delete
  const handleUpdateStatus = (statusType) => {
    setDisabled(true);
    setTimeout(() => {
      setDisabled(false); // Re-enable after 4 seconds
    }, 4000);

    const statusMap = {
      delete: 3,
      active: 1,
      inactive: 2,
    };

    const toastMessage = {
      delete: "Holiday deleted successfully",
      active: "Holiday activated successfully",
      inactive: "Holiday deactivated successfully",
    };

    const statusValue = statusMap[statusType];
    if (!statusValue) return;
    const id = !isEmpty(selectedRow) ? selectedRow : [holidayId];
    const payload = {
      id: id, // use selectedRow instead of hardcoded ID
      status: statusValue,
    };
    api
      .post(`/protected/deleteholiday`, payload)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          specificDispatch(masterAction.loadMasterHoliday({ status: "BOTH" }));
          toast.success(toastMessage[statusType], { containerId: "toast-1" });
          setSelectedRow([]);
          setHolidayId(null);
          handleModalClose();
        } else {
          toast.warning(response?.data?.msg || "");
        }
        return response.data;
      })
      .catch((err) => {
         setSelectedRow([]);
          handleModalClose();
        const errorMsg =
        err?.response?.data?.error||err?.response?.data?.message ||
          "An error occurred while deleting the holiday details";
        toast.error(errorMsg);
        return Promise.reject(err);
      });
  };

  const tableData = !isEmpty(masterHolidayList)
    ? masterHolidayList.map((data, index) => ({
        _id: data._id,
        sno: index + 1,
        holidayName: data.holidayName || "--",
        date: moment(data.date).format("DD/MM/YYYY") || "--",
        day: data.day || "--",
        // date: data.date || "--",
        description: data.description || "--",
        status: data.status === 1 ? "Active" : "Inactive",
      }))
    : [];

  const handleDateChange = (name, value) => {
    setHoliday((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [searchYear, setSearchYear] = useState(String(currentYear));
  const yearOptions = Array.from({ length: 3 }, (_, i) => currentYear - 1 + i); // Example: 2024, 2025, 2026
  const handleSearchMenuClick = (e) => {
    const year = e.key;
    setSearchYear(year);
    dispatch(masterAction.loadMasterHoliday({ year: year, status: "BOTH" }));
  };
  const searchMenu = (
    <Menu onClick={handleSearchMenuClick} style={{ minWidth: 200 }}>
      {yearOptions.map((year) => (
        <Menu.Item key={year}>{year}</Menu.Item>
      ))}
    </Menu>
  );

  return (
    <>
      <div className="pl-5">
        <MetaTags>
          <title>Holiday | Mequals</title>
        </MetaTags>
      </div>
      <Row>
        <BreadcrumbComponent title="Holiday" items={BCrumb} />
      </Row>

      <div
        style={{
          background: "#ffffff",
          margin: "4px 8px",
          padding: "16px",
          borderRadius: "8px 8px 0px 0px",
        }}
      >
        <Row gutter={[16, 16]} style={{ marginTop: "2px" }}>
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Typography className="poppins-txt">
              Year
              <span style={{ color: "red", marginLeft: "2px" }}>*</span>
            </Typography>
            <div className="mr-5 mt-2">
              <Dropdown overlay={menu} trigger={["click"]}>
                <Button
                  icon={<CalendarOutlined />}
                  style={{ width: "100%", justifyContent: "start" }}
                >
                  {selectedYear}
                </Button>
              </Dropdown>
            </div>
            <span
              style={{
                color: "#ef3022",
                fontSize: "11px",
                marginTop: "8px",
                fontFamily: "poppins",
                display: "block",
              }}
            >
              {simpleValidator.current.message(
                "selectedYear",
                selectedYear,
                "required"
              )}
            </span>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Typography className="poppins-txt">
              Date
              <span style={{ color: "red", marginLeft: "2px" }}>*</span>
            </Typography>
            <DatePicker
              style={{
                width: "100%",
                marginTop: "5px",
              }}
              className="custom-select"
              name="date"
              value={holiday.date}
              onChange={(date) => handleDateChange("date", date)}
              autoComplete="off"
            />
            <span
              style={{
                color: "#ef3022",
                fontSize: "11px",
                marginTop: "8px",
                fontFamily: "poppins",
                display: "block",
              }}
            >
              {simpleValidator.current.message(
                "date",
                holiday.date,
                "required"
              )}
            </span>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Typography className="poppins-txt">
              Holiday Name
              <span style={{ color: "red", marginLeft: "2px" }}>*</span>
            </Typography>
            <Input
              placeholder="Enter Holiday Name"
              allowClear
              style={{ width: "100%", fontFamily: "Poppins", marginTop: "5px" }}
              className="custom-select"
              name="holidayName"
              type="text"
              value={holiday.holidayName}
              onChange={(e) => handleInput(e)}
              autoComplete="off"
            />
            <span
              style={{
                color: "#ef3022",
                fontSize: "11px",
                marginTop: "8px",
                fontFamily: "poppins",
                display: "block",
              }}
            >
              {simpleValidator.current.message(
                "holidayName",
                holiday.holidayName,
                "required"
              )}
            </span>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Typography className="poppins-txt">Description</Typography>
            <Input
              placeholder="Enter Description"
              allowClear
              style={{ width: "100%", fontFamily: "Poppins", marginTop: "5px" }}
              name="description"
              className="custom-select"
              type="text"
              value={holiday.description}
              onChange={(e) => handleInput(e)}
              autoComplete="off"
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <div
              className="mt-1"
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                className="custom-btn"
                size="middle"
                type="primary"
                style={{ marginRight: "12px" }}
                onClick={handleSubmit}
                disabled={disabled}
                loading={addLoading}
              >
                {isEdit ? (
                  "Update"
                ) : (
                  <span>
                    <PlusOutlined></PlusOutlined>Add
                  </span>
                )}
              </Button>
              <Button
                className="custom-btn"
                style={{ background: "#FCE05A", color: "white" }}
                size="middle"
                htmlType="submit"
                onClick={onReset}
                loading={resetLoading}
              >
                <ClearOutlined />
                Clear
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      <div
        style={{
          background: "#ffffff",
          margin: "20px 8px 15px 8px",
          padding: "16px",
        }}
      >
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          {/* Left side: Search bar */}
          <Col>
            <div className="mt-2" style={{ display: "flex", gap: "8px" }}>
              <Dropdown overlay={searchMenu} trigger={["click"]}>
                <Button
                  icon={<CalendarOutlined />}
                  style={{ width: 200, justifyContent: "start" }}
                >
                  {searchYear}
                </Button>
              </Dropdown>

              <Button
                icon={<SearchOutlined />}
                type="primary"
                onClick={handleSearch}
              />
            </div>
          </Col>

          <Col>
            <Space>
              <Button
                className="custom-btn"
                size="medium"
                type="primary"
                style={{
                  backgroundColor: "#fc3535",
                  color: "#fff",
                  border: "none",
                }}
                icon={<DownloadOutlined />}
                onClick={exportToPdf}
                loading={downloadLoader}
              >
                Download PDF
              </Button>
              <Button
                className="custom-btn"
                size="medium"
                type="primary"
                icon={<DownloadOutlined />}
                onClick={exportToCsv}
                loading={downloadLoader}
              >
                Download
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          loading={searchLoading}
          dataSource={tableData}
          className="poppins-txt"
          rowKey={(record) => record._id}
          rowClassName={"text-[12px]"}
          pagination={{
            // make sure you provide total count from API
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "30"],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
          sortDirections={["ascend", "descend"]}
          showSorterTooltip={"sorter-icon"}
          scroll={{
            x: 765,
          }}
          rowSelection={{
            selectedRowKeys: selectedRow,
            preserveSelectedRowKeys: true,
            onSelectAll: (selected, selectedRows) => {
              const newSelecteds = selected
                ? selectedRows.map((n) => n._id)
                : [];
              setSelectedRow(newSelecteds);
            },
            onSelect: (record, selected) => {
              let newSelecteds = selected
                ? [...selectedRow, record._id]
                : selectedRow.filter((id) => id !== record._id);
              setSelectedRow(newSelecteds);
            },
          }}
        />

        <Space size={4}>
          <Button
            style={{
              marginTop: "20px",
              marginRight: "12px",
              background: "#f46a6a",
            }}
            type="primary"
            size="medium"
            className="custom-btn"
            onClick={() => showModalOpen("delete")}
          >
            Delete
          </Button>

          <Button
            style={{
              marginTop: "20px",
              background: "#34c38f",
              marginRight: "12px",
            }}
            type="primary"
            size="medium"
            className="custom-btn"
            onClick={() => showModalOpen("active")}
          >
            Active
          </Button>

          <Button
            style={{
              marginTop: "20px",
            }}
            type="primary"
            size="medium"
            className="custom-btn"
            onClick={() => showModalOpen("inactive")}
          >
            Inactive
          </Button>
        </Space>

        <Modal
          open={isModalVisible}
          onCancel={handleModalClose}
          title={"Confirmation"}
          cancelText="Cancel"
          className="poppins-txt custom-active-modal"
          centered
          width={450}
          footer={null} // Remove the default footer
          // style={{ padding: 0, marginLeft: "600px" }} // Remove padding from modal
        >
          <div className="custom-modal-content">
            <p>Are you sure you want to {modalType} this Holiday?</p>
          </div>

          {/* Custom footer with cancel and confirm buttons */}
          <div
            className="custom-modal-footer"
            style={{
              borderTop: "1px solid #ccc",
              padding: "10px 16px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              className="custom-btn"
              onClick={handleModalClose}
              style={{
                backgroundColor: "#f46a6a",
                color: "#fff",
                border: "none",
                marginRight: "10px",
              }}
            >
              Close
            </Button>
            <Button
              className="custom-btn"
              disabled={disabled}
              // onClick={handleModalClose}
              onClick={() => handleUpdateStatus(modalType)}
              style={{
                backgroundColor: "#34c38f",
                color: "#fff",
                border: "none",
              }}
            >
              Confirm
            </Button>
          </div>
        </Modal>
      </div>
      <ToastContainer
        containerId="toast-1"
        position="top-right"
        autoClose={1500}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        style={{
          fontSize: "12px",
        }}
        pauseOnHover
        theme="dark"
      />
    </>
  );
};

export default Holiday;
